import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { socket } from '../socket';

// Public page — students don't need an account to join.
export default function JoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState(searchParams.get('code') || '');
  const [playerName, setPlayerName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(
    searchParams.get('kicked') ? 'You were removed from the lobby by the teacher.' : ''
  );

  // Disconnect any lingering socket on mount (e.g. browser back from bingo card)
  useEffect(() => {
    if (socket.connected) socket.disconnect();
  }, []);

  function handleJoin(e) {
    e.preventDefault();
    const trimmedCode = code.trim().toUpperCase();
    const trimmedName = playerName.trim();

    if (!trimmedCode || trimmedCode.length !== 6) {
      setError('Enter your 6-character room code.');
      return;
    }
    if (!trimmedName) {
      setError('Enter your name.');
      return;
    }

    setError('');
    setJoining(true);
    socket.connect();

    // Each attempt removes the other attempt's listener when it fires, so a
    // retry after a connect error never leaves a stale connect listener behind
    // (which would send game:join twice).
    function onConnect() {
      socket.off('connect_error', onConnectError);
      socket.emit('game:join', { code: trimmedCode, playerName: trimmedName }, (res) => {
        if (res.error) {
          setError(res.error);
          setJoining(false);
          socket.disconnect();
        } else {
          // Pass board and game state via location state
          navigate(`/bingo/${trimmedCode}`, {
            state: {
              board: res.board,
              boardSize: res.boardSize || 5,
              freeSpace: res.freeSpace !== false,
              displayMode: res.displayMode || 'word',
              pattern: res.pattern || 'line',
              novelTitle: res.novelTitle || 'Vocabulary Bingo',
              markedCells: res.markedCells || [],
              strikes: res.strikes || 0,
              lockedOut: res.lockedOut || false,
              hasWon: res.hasWon || false,
              rejoinToken: res.rejoinToken,
              calledWords: res.calledWords,
              status: res.status,
              teacherName: res.teacherName,
              playerName: trimmedName,
            },
          });
        }
      });
    }

    function onConnectError() {
      socket.off('connect', onConnect);
      socket.disconnect();
      setError('Could not connect to the game server. Please try again.');
      setJoining(false);
    }

    socket.once('connect', onConnect);
    socket.once('connect_error', onConnectError);
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>🎲</div>
        <h1 style={styles.heading}>Join Bingo</h1>
        <p style={styles.sub}>Enter the room code your teacher shared.</p>

        <form onSubmit={handleJoin} style={styles.form}>
          <label style={styles.label}>Room Code</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={6}
            style={styles.codeInput}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
          />

          <label style={styles.label}>Your Name</label>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="e.g. Olivia"
            maxLength={40}
            style={styles.input}
          />

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            disabled={joining}
            style={{ ...styles.joinBtn, ...(joining ? styles.joinBtnDisabled : {}) }}
          >
            {joining ? 'Joining…' : 'Join Game →'}
          </button>
        </form>

        <p style={styles.footer}>
          Are you a teacher?{' '}
          <a href="/login" style={styles.footerLink}>Sign in →</a>
        </p>
        <p style={styles.footer}>
          <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', backgroundColor: 'var(--color-background)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px', fontFamily: 'system-ui, sans-serif',
  },
  card: {
    backgroundColor: 'var(--color-background)', borderRadius: '16px', border: '1px solid #e5e0d5',
    padding: '40px 36px', width: '100%', maxWidth: '400px', textAlign: 'center',
  },
  logo: { fontSize: '40px', marginBottom: '12px' },
  heading: { fontSize: '26px', fontWeight: '700', color: 'var(--color-text)', margin: '0 0 6px' },
  sub: { fontSize: '15px', color: '#6b6b7a', margin: '0 0 28px' },
  form: { textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  codeInput: {
    padding: '12px 16px', fontSize: '22px', fontWeight: '700', letterSpacing: '0.18em',
    border: '2px solid #e5e0d5', borderRadius: '8px', width: '100%',
    boxSizing: 'border-box', outline: 'none', textAlign: 'center',
    marginBottom: '16px', fontFamily: 'monospace', textTransform: 'uppercase',
  },
  input: {
    padding: '11px 14px', fontSize: '15px', border: '2px solid #e5e0d5',
    borderRadius: '8px', width: '100%', boxSizing: 'border-box', outline: 'none',
    marginBottom: '16px',
  },
  error: { color: '#dc2626', fontSize: '13px', margin: '0 0 12px' },
  joinBtn: {
    padding: '13px', backgroundColor: 'var(--color-primary)', color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', width: '100%',
  },
  joinBtnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  footer: { fontSize: '13px', color: '#6b6b7a', marginTop: '24px' },
  footerLink: { color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none' },
};
